/**
 * ScanTable — scans list table (CSS Modules pilot).
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTableColumnResize } from '../../../../utils/tableColumnResize';
import {
  getScansTableColumns,
  SCANS_TABLE_COLUMN_WIDTHS_KEY_NETWORK,
  SCANS_TABLE_COLUMN_WIDTHS_KEY_WEB,
} from '../../../../utils/resizableTableColumns';
import { ResizableColgroup, ResizableTableHead } from '../../../../components/ResizableTableHead';
import BlueXLoader from '../../../../components/BlueXLoader';
import { TrashIcon } from '../../../../shared/components/TrashIcon';
import TablePagination, { TABLE_PAGE_SIZE, usePaginatedRows } from '../../../../shared/components/TablePagination';
import {
  numOr0,
  clampProgressPercent,
  normalizeScansRow,
  getScansLifecycleKind,
  getScansStatusKind,
  scansLifecycleHidesRisks,
  scansSearchHaystack,
  formatScanDateTime,
  NO_LIVE_HOSTS_NOTE,
} from '../../scans.utils';
import '../../../vulnerabilities/vulnerabilities.css';
import styles from './ScanTable.module.css';

const SEVERITY_COLORS = {
  critical: '#991B1B',
  high: '#C75E1A',
  medium: '#D4A017',
  low: '#2563EB',
};

const UNIVERSAL_SEARCH_MAX_LEN = 500;

function sanitizeUniversalSearchInput(raw) {
  return String(raw ?? '')
    .replace(/\0/g, '')
    .slice(0, UNIVERSAL_SEARCH_MAX_LEN);
}

const RISK_SLOT_LABELS = ['Critical', 'High', 'Medium', 'Low'];

const ScansRiskSlot = ({ label, count, color, showLabel = false }) => {
  const n = numOr0(count);
  const empty = n === 0;
  return (
    <div className={styles.riskSlot} title={`${label}: ${n}`}>
      {showLabel ? <span className={styles.riskLabel}>{label}</span> : null}
      <span className={styles.riskSlotMain}>
        <span
          className={`${styles.riskDot} ${empty ? styles.riskDotEmpty : styles.riskDotFilled}`}
          style={empty ? undefined : { backgroundColor: color }}
          aria-hidden="true"
        />
        <span className={[styles.riskCount, empty ? styles.isEmpty : null].filter(Boolean).join(' ')}>
          {n}
        </span>
      </span>
    </div>
  );
};

const ScansRisksHeaderLabels = () => (
  <div className={[styles.riskGrid, styles.risksThLabels].filter(Boolean).join(' ')} aria-hidden="true">
    {RISK_SLOT_LABELS.map((label) => (
      <span key={label} className={[styles.riskSlot, styles.risksThLabelSlot].filter(Boolean).join(' ')}>
        <span className={styles.riskLabel}>{label}</span>
      </span>
    ))}
  </div>
);

const ScansRisksCell = ({ row }) => {
  if (row.risksAvailable === false) {
    return (
      <span className={styles.risksNa} aria-label="Severity results are not available yet">
        —
      </span>
    );
  }
  const c = row.riskCritical ?? 0;
  const h = row.riskHigh ?? 0;
  const m = row.riskMedium ?? 0;
  const l = row.riskLow ?? 0;
  // Informational findings (technologies, banners, headers) are observations, not
  // vulnerabilities: shown on their own, never counted as a severity.
  const info = row.riskInfo ?? 0;
  const summary = `Critical ${c}, High ${h}, Medium ${m}, Low ${l}`;
  return (
    <div className={styles.riskWithObservations}>
    <div className={[styles.riskBreakdown, styles.riskGrid].filter(Boolean).join(' ')} role="img" aria-label={summary}>
      <ScansRiskSlot label="Critical" count={c} color={SEVERITY_COLORS.critical} />
      <ScansRiskSlot label="High" count={h} color={SEVERITY_COLORS.high} />
      <ScansRiskSlot label="Medium" count={m} color={SEVERITY_COLORS.medium} />
      <ScansRiskSlot label="Low" count={l} color={SEVERITY_COLORS.low} />
    </div>
    {info > 0 ? (
      <span className={styles.riskObservations} title="Informational findings: observations such as technologies and headers, not vulnerabilities">
        {info} informational
      </span>
    ) : null}
    </div>
  );
};

const ScanDateTimeCell = ({ value, timeZone, emptyLabel = '—' }) => {
  const formatted = formatScanDateTime(value, timeZone);
  if (!value) return <span>{emptyLabel}</span>;
  const timeLine = [formatted.time, formatted.timeZoneLabel].filter(Boolean).join(' ');
  return (
    <span className={styles.dateTimeStack} title={formatted.title}>
      <span className={styles.dateTimeDate}>{formatted.date || formatted.display}</span>
      {timeLine ? <span className={styles.dateTimeTime}>{timeLine}</span> : null}
    </span>
  );
};

const ScansNetworkTypeTag = ({ value }) => {
  const raw = String(value || '').trim();
  if (!raw) return <span className={styles.networkTypeEmpty}>—</span>;

  const kind = raw.toLowerCase().includes('perimeter') ? 'perimeter' : 'on_premise';
  const label = kind === 'perimeter' ? 'Perimeter' : 'On-premise';

  return (
    <span
      className={[
        styles.networkType,
        kind === 'perimeter' ? styles.networkTypePerimeter : styles.networkTypeOnPremise,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {label}
    </span>
  );
};

// Failed scans carry "XS-AREA-NNN: customer message" (backend common/error_codes.py).
const SCAN_ERROR_CODE = /^(XS-[A-Z]+-\d{3}):\s*(.*)$/s;

export const parseScanErrorCode = (reason) => {
  const match = SCAN_ERROR_CODE.exec(String(reason || '').trim());
  return match ? { code: match[1], message: match[2] } : null;
};

export const ScansStatusTag = ({ status, taskLifecycle, reason }) => {
  const kind = getScansStatusKind(status, null, taskLifecycle);
  const raw = String(status || '').trim();

  if (kind === 'completed') {
    const noLiveHosts = String(reason || '').trim() === NO_LIVE_HOSTS_NOTE;
    return (
      <span
        className={[styles.statusTag, styles.statusTagCompleted].filter(Boolean).join(' ')}
        title={noLiveHosts ? 'The scanner did not find any live hosts. Check the target is online and reachable from the scanner.' : undefined}
      >
        <span className={styles.statusTagLabel}>Completed</span>
        {noLiveHosts ? <span className={styles.statusTagStage}>No live hosts</span> : null}
      </span>
    );
  }
  if (kind === 'in_progress') {
    return (
      <span className={[styles.statusTag, styles.statusTagInProgress].filter(Boolean).join(' ')}>
        <span className={styles.statusTagLabel}>In progress</span>
        <span className={styles.statusTagLoader} aria-hidden="true" />
      </span>
    );
  }
  if (kind === 'scheduled') {
    return (
      <span className={[styles.statusTag, styles.statusTagScheduled].filter(Boolean).join(' ')}>
        <span className={styles.statusTagLabel}>Scheduled</span>
      </span>
    );
  }
  if (kind === 'failed') {
    const error = parseScanErrorCode(reason);
    return (
      <span
        className={[styles.statusTag, styles.statusTagFailed].filter(Boolean).join(' ')}
        title={error ? `${error.code}: ${error.message} Quote this code when contacting support.` : undefined}
      >
        <span className={styles.statusTagLabel}>Failed</span>
        {error ? <span className={styles.statusTagStage}>{error.code}</span> : null}
      </span>
    );
  }
  if (kind === 'recovering') {
    return (
      <span className={[styles.statusTag, styles.statusTagRecovering].filter(Boolean).join(' ')} title={raw || 'Recovering — scan will resume shortly'}>
        <span className={styles.statusTagLabel}>Recovering</span>
      </span>
    );
  }

  return (
    <span className={[styles.statusTag, styles.statusTagNeutral].filter(Boolean).join(' ')} title={raw || undefined}>
      {raw || ''}
    </span>
  );
};

// Stage labels the backend records while a web scan runs (task_status_reason).
const SCAN_STAGE_LABELS = new Set([
  'Queued',
  'Starting scan',
  'Crawling site',
  'Crawling JavaScript pages',
  'Active scanning',
  'Saving results',
]);

const ScansStatusCell = ({ status, progressPercent, taskLifecycle, stage }) => {
  const kind = getScansLifecycleKind(status, progressPercent, taskLifecycle);
  if (kind !== 'in_progress') {
    return <ScansStatusTag status={status} taskLifecycle={taskLifecycle} reason={stage} />;
  }

  // Same pill chrome as Completed / Failed — percent when known; otherwise a loader.
  const normalizedProgress = clampProgressPercent(progressPercent);
  const roundedProgress = normalizedProgress == null ? null : Math.round(normalizedProgress);
  const showPercent = roundedProgress != null && roundedProgress > 0;
  const label = showPercent ? `In progress ${roundedProgress}%` : 'In progress';
  const raw = String(status || '').trim();

  const stageLabel = SCAN_STAGE_LABELS.has(String(stage || '').trim()) ? String(stage).trim() : '';

  const fullLabel = stageLabel ? `${label}, ${stageLabel}` : label;

  return (
    <span
      className={[styles.statusTag, styles.statusTagInProgress].filter(Boolean).join(' ')}
      title={raw || fullLabel}
      role="status"
      aria-label={showPercent ? fullLabel : 'In progress, waiting for percent'}
      aria-busy={!showPercent}
    >
      <span className={styles.statusTagLabel}>{label}</span>
      {showPercent ? null : <span className={styles.statusTagLoader} aria-hidden="true" />}
      {stageLabel ? <span className={styles.statusTagStage}>{stageLabel}</span> : null}
    </span>
  );
};

export default function ScanTable({
  data: propData,
  scanType,
  isLoading = false,
  displayTimezone = null,
  hideNetworkTypeColumn = false,
  showScanKindColumn = false,
  showCheckboxColumn = true,
  toolbarSearch = false,
  toolbarSearchPlaceholder = 'Search scans',
  sectionHeading = null,
  sectionHeadingId = null,
  maxVisibleRows: _maxVisibleRows = null,
  universalSearchQuery = '',
  onUniversalSearchQueryChange = undefined,
  toolbarLeadingContent = null,
  onTargetClick = null,
  allowActiveTargetClick = false,
  onRerunScan = null,
  onStopScan = null,
  onPauseScan = null,
  onResumeScan = null,
  onDeleteScan = null,
  onEditSchedule = null,
  onRunSchedule = null,
  recordView = 'runs',
  itemLabel = 'scan runs',
  scanSocketState = 'disconnected',
  emptyMessage: emptyMessageProp = '',
  loadFailed = false,
}) {
  const [sortConfig, setSortConfig] = useState(() => (
    recordView === 'schedules'
      ? { key: 'nextRunAt', direction: 'asc' }
      : { key: 'completedAt', direction: 'desc' }
  ));
  const pageSize = TABLE_PAGE_SIZE;
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [bulkActionBusy, setBulkActionBusy] = useState(false);
  const [headerGutterPx, setHeaderGutterPx] = useState(8);
  const [openRowMenuKey, setOpenRowMenuKey] = useState(null);
  const [rowMenuPlacement, setRowMenuPlacement] = useState(null);
  const selectAllRef = useRef(null);
  const tableHeaderRef = useRef(null);
  const tableScrollRef = useRef(null);
  const rowActionsMenuRef = useRef(null);
  const rowActionsTriggerRef = useRef(null);
  const tourScanRowsRef = useRef([]);
  const tableSearchId = useId();
  const useUniversalToolbarSearch =
    Boolean(toolbarSearch) && typeof onUniversalSearchQueryChange === 'function';
  const searchQuery = useUniversalToolbarSearch ? universalSearchQuery : internalSearchQuery;
  const applyToolbarSearch = (raw) => {
    const next = sanitizeUniversalSearchInput(raw);
    if (useUniversalToolbarSearch) onUniversalSearchQueryChange(next);
    else setInternalSearchQuery(next);
  };
  const fallbackSectionHeadingId = useId();

  const closeRowActionsMenu = useCallback(() => {
    setOpenRowMenuKey(null);
    setRowMenuPlacement(null);
  }, []);

  const rows = useMemo(() => {
    if (isLoading) return [];
    if ((scanType === 'network' || scanType === 'web') && Array.isArray(propData)) {
      return propData.map((r) => {
        const normalized = normalizeScansRow(r);
        // Keep the original API/history row so schedule Run/Edit can recover site + scanner.
        return { ...normalized, __source: r };
      });
    }
    return [];
  }, [propData, scanType, isLoading]);

  const showNetworkTypeColumn = scanType === 'network' && !hideNetworkTypeColumn;
  const showScanKind = Boolean(showScanKindColumn);
  const showCheckboxes = Boolean(showCheckboxColumn);

  const filteredRows = useMemo(() => {
    if (!toolbarSearch) return rows;
    const q = String(searchQuery || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => scansSearchHaystack(r).includes(q));
  }, [rows, toolbarSearch, searchQuery]);

  const sortedRows = useMemo(() => {
    const compareByKey = (a, b, key) => {
      if (key === 'risks') {
        const aHide = scansLifecycleHidesRisks(a.status, a.progressPercent);
        const bHide = scansLifecycleHidesRisks(b.status, b.progressPercent);
        const aVal = aHide ? null : Number(a.risks) || 0;
        const bVal = bHide ? null : Number(b.risks) || 0;
        if (aVal === null && bVal === null) return 0;
        if (aVal === null) return 1;
        if (bVal === null) return -1;
        return aVal - bVal;
      }
      if (key === 'scanDate' || key === 'scanTime' || key === 'startedAt' || key === 'completedAt' || key === 'nextRunAt') {
        const resolveTime = (row) => {
          if (key === 'nextRunAt') return Date.parse(String(row.nextRunAt || '')) || 0;
          if (key === 'completedAt') {
            return (
              Date.parse(String(row.completedAt || ''))
              || Date.parse(String(row.startedAt || ''))
              || Date.parse(String(row.scanDate || ''))
              || 0
            );
          }
          if (key === 'startedAt') {
            return Date.parse(String(row.startedAt || row.scanDate || '')) || 0;
          }
          return Date.parse(String(row[key] || row.scanDate || '')) || 0;
        };
        return resolveTime(a) - resolveTime(b);
      }
      if (key === 'orgName') {
        return `${a.orgName || ''} ${a.siteName || ''}`.localeCompare(`${b.orgName || ''} ${b.siteName || ''}`, undefined, { sensitivity: 'base' });
      }
      const aStr = String(a?.[key] ?? '').toLowerCase();
      const bStr = String(b?.[key] ?? '').toLowerCase();
      return aStr.localeCompare(bStr, undefined, { sensitivity: 'base' });
    };

    const lifecycleSortRank = (row) => {
      const kind = getScansLifecycleKind(row.status, row.progressPercent, row.taskLifecycle);
      if (kind === 'in_progress') return 0;
      if (kind === 'recovering') return 1;
      if (kind === 'failed') return 2;
      if (kind === 'completed') return 3;
      if (kind === 'scheduled') return 4;
      return 5;
    };

    const items = filteredRows.map((r, idx) => ({ r, idx }));
    items.sort((x, y) => {
      const result = compareByKey(x.r, y.r, sortConfig.key);
      if (result !== 0) return sortConfig.direction === 'asc' ? result : -result;

      const byLifecycle = lifecycleSortRank(x.r) - lifecycleSortRank(y.r);
      if (byLifecycle !== 0) return byLifecycle;

      return x.idx - y.idx;
    });
    return items.map((it) => it.r);
  }, [filteredRows, sortConfig]);
  const scanPagination = usePaginatedRows(sortedRows, pageSize);
  const visibleRows = scanPagination.pageRows;
  tourScanRowsRef.current = visibleRows;

  const handleSort = (key) => {
    const dateKeys = new Set(['scanDate', 'scanTime', 'startedAt', 'completedAt', 'nextRunAt']);
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: dateKeys.has(key) ? 'desc' : 'asc' };
    });
  };

  useEffect(() => {
    setSortConfig(
      recordView === 'schedules'
        ? { key: 'nextRunAt', direction: 'asc' }
        : { key: 'completedAt', direction: 'desc' }
    );
  }, [recordView]);

  useEffect(() => {
    if (!openRowMenuKey) return undefined;
    const onPointerDown = (event) => {
      const inMenu = rowActionsMenuRef.current?.contains(event.target);
      const inTrigger = rowActionsTriggerRef.current?.contains(event.target);
      if (!inMenu && !inTrigger) closeRowActionsMenu();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeRowActionsMenu();
    };
    const onRepositionClose = () => closeRowActionsMenu();
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onRepositionClose);
    const scrollEl = tableScrollRef.current;
    scrollEl?.addEventListener('scroll', onRepositionClose, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onRepositionClose);
      scrollEl?.removeEventListener('scroll', onRepositionClose);
    };
  }, [openRowMenuKey, closeRowActionsMenu]);

  useEffect(() => {
    closeRowActionsMenu();
  }, [scanPagination.page, sortConfig.key, sortConfig.direction, recordView, closeRowActionsMenu]);

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '⇅';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const getScanDateParts = (value) => {
    if (!value) return { date: '', time: '' };
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      const raw = String(value);
      return { date: raw.slice(0, 10), time: raw.slice(11, 19) };
    }
    const tz = String(displayTimezone || '').trim();
    const dateOptions = { day: '2-digit', month: 'short', year: '2-digit' };
    const timeOptions = {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    };
    const readParts = (fmtOptions) => {
      const parts = new Intl.DateTimeFormat('en-US', fmtOptions).formatToParts(d);
      const get = (type) => parts.find((p) => p.type === type)?.value || '';
      return {
        day: get('day'),
        month: get('month').replace(/\.$/, ''),
        year: get('year'),
        hour: get('hour'),
        minute: get('minute'),
        second: get('second'),
        dayPeriod: get('dayPeriod'),
      };
    };
    const toDisplay = (opts) => {
      const p = readParts(opts);
      const date = p.day && p.month && p.year ? `${p.day}-${p.month}-${p.year}` : '';
      const time =
        p.hour && p.minute && p.second && p.dayPeriod
          ? `${p.hour}:${p.minute}:${p.second} ${p.dayPeriod}`
          : '';
      return { date, time };
    };
    if (tz) {
      try {
        const withTz = toDisplay({ ...dateOptions, ...timeOptions, timeZone: tz });
        if (withTz.date || withTz.time) return withTz;
      } catch (_) {
        // Fallback to browser-local formatting for invalid/unsupported timezone values.
      }
    }
    return toDisplay({ ...dateOptions, ...timeOptions });
  };
  const isNetworkScan = scanType === 'network';

  const rowKey = (r) => r.scanId || r.target || '';

  useEffect(() => {
    const selectFirst = () => {
      const row = tourScanRowsRef.current[0];
      if (!row) return;
      setSelectedKeys(new Set([rowKey(row)]));
    };
    const clearSelection = () => {
      setSelectedKeys(new Set());
    };
    window.addEventListener('xspectre:tour-select-first-scan', selectFirst);
    window.addEventListener('xspectre:tour-clear-scan-selection', clearSelection);
    return () => {
      window.removeEventListener('xspectre:tour-select-first-scan', selectFirst);
      window.removeEventListener('xspectre:tour-clear-scan-selection', clearSelection);
    };
  }, []);

  const toggleRow = (key) => setSelectedKeys((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  useEffect(() => {
    const el = selectAllRef.current;
    if (!el) return;
    const total = visibleRows.length;
    const sel = visibleRows.filter((r) => selectedKeys.has(rowKey(r))).length;
    el.checked = total > 0 && sel === total;
    el.indeterminate = sel > 0 && sel < total;
  }, [selectedKeys, visibleRows]);
  const toggleSelectAll = () => {
    const visibleKeys = visibleRows.map(rowKey);
    const allSelected = visibleKeys.every((k) => selectedKeys.has(k));
    setSelectedKeys(allSelected ? new Set() : new Set(visibleKeys));
  };
  const selectedRows = sortedRows.filter((r) => selectedKeys.has(rowKey(r)));
  const rerunableSelected = selectedRows.filter((r) => {
    const k = getScansLifecycleKind(r.status, r.progressPercent, r.taskLifecycle);
    return k === 'failed' || k === 'recovering';
  });
  const stoppableSelected = selectedRows.filter((r) => {
    const k = getScansLifecycleKind(r.status, r.progressPercent, r.taskLifecycle);
    return k === 'in_progress';
  });
  const scanTableColumns = getScansTableColumns({
    showNetworkTypeColumn,
    showScanKindColumn: showScanKind,
    showCheckboxColumn: showCheckboxes,
    recordView,
    showOrgSiteColumn: true,
  });
  if (showCheckboxes) {
    scanTableColumns[0].headerContent = (
      <span data-tour="scans-select-all">
        <input
          type="checkbox"
          ref={selectAllRef}
          className="risks-page-checkbox"
          aria-label={`Select all visible ${itemLabel} on this page`}
          onChange={toggleSelectAll}
        />
      </span>
    );
  }
  const risksColumn = scanTableColumns.find((col) => col.key === 'risks');
  if (risksColumn) {
    risksColumn.renderHeader = ({
      col,
      sortConfig: headerSortConfig,
      onSort,
      getSortIcon,
      showResizeHandle,
      onResizeStart,
      resizingKey,
      resizeHandleClassName,
      defaultSortButtonClassName,
      defaultSortIconClassName,
    }) => {
      const sortBtnClass = col.sortButtonClassName || defaultSortButtonClassName;
      const sortIconClass = col.sortIconClassName || defaultSortIconClassName;
      return (
        <>
          <div className={styles.risksThStack}>
            {col.sortKey && onSort ? (
              <button type="button" className={sortBtnClass} onClick={() => onSort(col.sortKey)}>
                {col.label}{' '}
                <span
                  className={`${sortIconClass} ${headerSortConfig?.key === col.sortKey ? 'is-active' : ''}`}
                >
                  {getSortIcon ? getSortIcon(col.sortKey) : '↕'}
                </span>
              </button>
            ) : (
              <span className="assets-th-label">{col.label}</span>
            )}
            <ScansRisksHeaderLabels />
          </div>
          {showResizeHandle ? (
            <span
              className={`${resizeHandleClassName}${resizingKey === col.key ? ' is-active' : ''}`}
              role="separator"
              aria-orientation="vertical"
              aria-label={`Resize ${col.label || col.key} column`}
              onMouseDown={(e) => onResizeStart(col.key, e)}
            />
          ) : null}
        </>
      );
    };
  }
  const {
    widths: scanColumnWidths,
    onResizeStart: onScanColumnResizeStart,
    tableMinWidth: scansTableMinWidth,
    resizingKey: scanColumnResizingKey,
  } = useTableColumnResize(
    scanTableColumns,
    isNetworkScan ? SCANS_TABLE_COLUMN_WIDTHS_KEY_NETWORK : SCANS_TABLE_COLUMN_WIDTHS_KEY_WEB
  );

  useLayoutEffect(() => {
    const body = tableScrollRef.current;
    const header = tableHeaderRef.current;
    if (!body || !header || isLoading) return undefined;

    const syncHeader = () => {
      if (header.scrollLeft !== body.scrollLeft) {
        header.scrollLeft = body.scrollLeft;
      }
    };

    // Native listener stays in lockstep with trackpad/momentum scroll (React onScroll can lag).
    body.addEventListener('scroll', syncHeader, { passive: true });
    syncHeader();
    return () => body.removeEventListener('scroll', syncHeader);
  }, [isLoading, scansTableMinWidth, visibleRows.length, showNetworkTypeColumn, showScanKind, recordView]);

  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    const CONTENT_INSET_PX = 16;

    const syncGutter = () => {
      const scrollbarLane = Math.max(0, Math.round(el.offsetWidth - el.clientWidth));
      const next = scrollbarLane + CONTENT_INSET_PX;
      setHeaderGutterPx((prev) => (prev === next ? prev : next));
    };

    syncGutter();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncGutter) : null;
    ro?.observe(el);
    window.addEventListener('resize', syncGutter);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', syncGutter);
    };
  }, [isLoading, sortedRows.length, showNetworkTypeColumn, showScanKind]);

  const emptyMessage = loadFailed
    ? (String(emptyMessageProp || '').trim() || `Unable to load ${itemLabel}.`)
    : String(emptyMessageProp || '').trim()
      ? String(emptyMessageProp).trim()
      : toolbarSearch && searchQuery.trim() && !isLoading && rows.length > 0 && sortedRows.length === 0
        ? `No ${itemLabel} match your search`
        : `No ${itemLabel} found`;

  const bulkActions = showCheckboxes && selectedRows.length > 0 ? (
    <div className={styles.bulkActions} role="group" aria-label="Bulk scan actions" data-tour="scans-bulk-actions">
      {onDeleteScan && (
        <button
          type="button"
          className={[styles.bulkBtn, styles.bulkDeleteBtn].filter(Boolean).join(' ')}
          disabled={bulkActionBusy}
          aria-label={`Delete ${selectedRows.length} selected ${itemLabel}`}
          title={`Delete ${selectedRows.length} selected ${itemLabel}`}
          onClick={async () => {
            if (bulkActionBusy) return;
            const scanEntries = selectedRows
              .map((r) => ({
                scanId: String(r.scanId || '').trim(),
                status: String(r.status || '').trim(),
                taskLifecycle: String(r.taskLifecycle || '').trim(),
                scanType,
              }))
              .filter((entry) => Boolean(entry.scanId));
            if (scanEntries.length === 0) return;
            setBulkActionBusy(true);
            try {
              await onDeleteScan(scanEntries);
              setSelectedKeys(new Set());
            } finally {
              setBulkActionBusy(false);
            }
          }}
        >
          {`Delete (${selectedRows.length})`}
        </button>
      )}
      {onRerunScan && rerunableSelected.length > 0 && (
        <button
          type="button"
          className={[styles.bulkBtn, styles.bulkRerunBtn].filter(Boolean).join(' ')}
          disabled={bulkActionBusy}
          onClick={async () => {
            if (bulkActionBusy) return;
            setBulkActionBusy(true);
            try {
              await Promise.allSettled(
                rerunableSelected
                  .map((r) => String(r.scanId || '').trim())
                  .filter(Boolean)
                  .map((id) => onRerunScan(id, scanType))
              );
              setSelectedKeys(new Set());
            } finally {
              setBulkActionBusy(false);
            }
          }}
        >
          Rerun selected ({rerunableSelected.length})
        </button>
      )}
      {onStopScan && stoppableSelected.length > 0 && (
        <button
          type="button"
          className={[styles.bulkBtn, styles.bulkStopBtn].filter(Boolean).join(' ')}
          disabled={bulkActionBusy}
          onClick={async () => {
            if (bulkActionBusy) return;
            setBulkActionBusy(true);
            try {
              await Promise.allSettled(
                stoppableSelected
                  .map((r) => ({
                    scanId: String(r.scanId || '').trim(),
                    scannerId: r.scannerId,
                  }))
                  .filter((item) => Boolean(item.scanId))
                  .map((item) => onStopScan(item.scanId, item.scannerId))
              );
              setSelectedKeys(new Set());
            } finally {
              setBulkActionBusy(false);
            }
          }}
        >
          Stop selected ({stoppableSelected.length})
        </button>
      )}
    </div>
  ) : null;

  const tableClassName = [
    'assets-table',
    styles.table,
    styles.tableResizable,
    isNetworkScan ? styles.tableNetwork : null,
    isNetworkScan && hideNetworkTypeColumn ? styles.tableNetworkNoType : null,
  ]
    .filter(Boolean)
    .join(' ');
  const tableStyle = { width: '100%', minWidth: scansTableMinWidth };

  const headerTable = (
    <table className={`${tableClassName} ${styles.tableHeader}`} style={tableStyle}>
      <ResizableColgroup columns={scanTableColumns} widths={scanColumnWidths} />
      <ResizableTableHead
        columns={scanTableColumns}
        widths={scanColumnWidths}
        onResizeStart={onScanColumnResizeStart}
        resizingKey={scanColumnResizingKey}
        sortConfig={sortConfig}
        onSort={handleSort}
        getSortIcon={getSortIcon}
        innerClassName="assets-th-inner"
        resizeHandleClassName={styles.colResizeHandle}
      />
    </table>
  );

  const bodyTable = (
    <table className={`${tableClassName} ${styles.tableBody}`} style={tableStyle}>
      <ResizableColgroup columns={scanTableColumns} widths={scanColumnWidths} />
      <tbody>
          {visibleRows.length > 0 ? (
            visibleRows.map((r, idx) => {
              const _lifecycle = getScansLifecycleKind(r.status, r.progressPercent, r.taskLifecycle);
              const _isClickable =
                typeof onTargetClick === 'function' &&
                (_lifecycle === 'completed' ||
                  _lifecycle === 'failed' ||
                  (allowActiveTargetClick && (_lifecycle === 'in_progress' || _lifecycle === 'scheduled')));
              return (
              <tr
                key={r.scanId || `${r.target}-${idx}`}
                className={[
                  _isClickable ? styles.rowClickable : null,
                  selectedKeys.has(rowKey(r)) ? styles.rowSelected : null,
                ].filter(Boolean).join(' ')}
                data-tour={idx === 0 ? 'scans-row' : undefined}
                onClick={
                  _isClickable
                    ? () => onTargetClick(r)
                    : undefined
                }
                onKeyDown={
                  _isClickable
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onTargetClick(r);
                        }
                      }
                    : undefined
                }
                tabIndex={_isClickable ? 0 : undefined}
                role={_isClickable ? 'button' : undefined}
              >
                {showCheckboxes ? (
                  <td className={styles.checkCell} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="risks-page-checkbox"
                      aria-label={`Select ${r.scanName || r.target || 'scan'}`}
                      checked={selectedKeys.has(rowKey(r))}
                      onChange={() => toggleRow(rowKey(r))}
                    />
                  </td>
                ) : null}
                <td className={styles.targetCell}>
                  {(() => {
                    const name = String(r.scanName || '').trim();
                    const target = String(r.target || '').trim();
                    const primary = name || target || '—';
                    const showTargetUnder = Boolean(name && target && name !== target);
                    const title = [name, target].filter(Boolean).join(' · ') || primary;
                    return (
                      <span className={styles.nameTargetStack} title={title}>
                        {_isClickable ? (
                          <button
                            type="button"
                            className={[styles.targetLink, styles.nameTargetName].filter(Boolean).join(' ')}
                            onClick={(e) => {
                              e.stopPropagation();
                              onTargetClick(r);
                            }}
                          >
                            {primary}
                          </button>
                        ) : (
                          <span className={styles.nameTargetName}>{primary}</span>
                        )}
                        {showTargetUnder ? (
                          <span className={styles.nameTargetTarget}>{target}</span>
                        ) : null}
                      </span>
                    );
                  })()}
                </td>
                <td className={styles.targetCell}>
                  {(() => {
                    const org = String(r.orgName || '').trim();
                    const site = String(r.siteName || '').trim();
                    if (!org && !site) {
                      return (
                        <span className={styles.targetText} title="Organization and site not provided">—</span>
                      );
                    }
                    const title = [org, site].filter(Boolean).join(' · ');
                    return (
                      <span className={styles.orgSiteStack} title={title}>
                        <span className={styles.orgSiteOrg}>{org || '—'}</span>
                        {site ? <span className={styles.orgSiteSite}>{site}</span> : null}
                      </span>
                    );
                  })()}
                </td>
                {showScanKind ? (
                  <td className={styles.scanKindCell}>
                    {String(r.scanType || scanType).toLowerCase() === 'network' ? (
                      <ScansNetworkTypeTag value={r.networkType} />
                    ) : (
                      <span className={styles.networkType}>Web</span>
                    )}
                  </td>
                ) : null}
                {showNetworkTypeColumn && (
                  <td className={styles.networkTypeCell}>
                    <ScansNetworkTypeTag value={r.networkType} />
                  </td>
                )}
                <td className={styles.statusCell}>
                  <ScansStatusCell status={r.status} progressPercent={r.progressPercent} scanSocketState={scanSocketState} taskLifecycle={r.taskLifecycle} stage={r.stage} />
                </td>
                {recordView === 'schedules' ? (
                  <>
                    <td>
                      <ScanDateTimeCell value={r.nextRunAt || r.scanDate} timeZone={displayTimezone} />
                    </td>
                    <td>{r.recurrence || '—'}</td>
                  </>
                ) : (
                  <>
                    <td>
                      <ScanDateTimeCell value={r.startedAt || r.scanDate} timeZone={displayTimezone} />
                    </td>
                    <td>
                      <ScanDateTimeCell value={r.completedAt} timeZone={displayTimezone} />
                    </td>
                    <td className={styles.riskCell}>
                      {scansLifecycleHidesRisks(r.status, r.progressPercent, r.taskLifecycle) ? (
                        <span className={styles.risksNa} aria-label="Severity results are unavailable until the scan completes">
                          —
                        </span>
                      ) : (
                        <div className={styles.risksColStack}>
                          <ScansRisksCell row={r} />
                        </div>
                      )}
                    </td>
                  </>
                )}
                <td className={styles.actionsCell}>
                  <div
                    className={styles.rowActions}
                    data-tour={idx === 0 ? 'scans-row-actions' : undefined}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {(() => {
                      const lifecycle = getScansLifecycleKind(r.status, r.progressPercent, r.taskLifecycle);
                      const isSchedule = recordView === 'schedules' || r.recordKind === 'schedule';
                      const menuKey = `${rowKey(r)}:${idx}`;
                      const menuOpen = openRowMenuKey === menuKey;
                      const canDelete = Boolean(r.scanId && onDeleteScan);
                      const canEditSchedule = isSchedule && Boolean(r.scanId && onEditSchedule);
                      const canRunSchedule = isSchedule && Boolean(r.scanId && onRunSchedule);
                      const canRerun =
                        !isSchedule &&
                        Boolean(r.scanId && onRerunScan) &&
                        (lifecycle === 'failed' || lifecycle === 'recovering' || lifecycle === 'completed');
                      const canStop = !isSchedule && Boolean(r.scanId && onStopScan) && lifecycle === 'in_progress';
                      const canPause = !isSchedule && Boolean(r.scanId && onPauseScan) && lifecycle === 'in_progress';
                      const canResume = isSchedule && Boolean(r.scanId && onResumeScan);
                      const menuItems = [];
                      const scheduleScanType =
                        String(r.scanType || scanType).toLowerCase() === 'network' ? 'network' : 'web';

                      if (canRunSchedule) {
                        menuItems.push({
                          key: 'run',
                          label: 'Run scan',
                          onSelect: () =>
                            onRunSchedule({
                              ...(r.__source && typeof r.__source === 'object' ? r.__source : {}),
                              ...r,
                              scanType: scheduleScanType,
                            }),
                        });
                      }
                      if (canEditSchedule) {
                        menuItems.push({
                          key: 'edit',
                          label: 'Edit',
                          onSelect: () =>
                            onEditSchedule({
                              ...(r.__source && typeof r.__source === 'object' ? r.__source : {}),
                              ...r,
                              scanType: scheduleScanType,
                            }),
                        });
                      }
                      if (canStop) {
                        menuItems.push({
                          key: 'stop',
                          label: 'Stop',
                          onSelect: () => onStopScan(r.scanId, r.scannerId),
                        });
                      }
                      if (canPause) {
                        menuItems.push({
                          key: 'pause',
                          label: 'Pause',
                          onSelect: () => onPauseScan(r.scanId, r.scannerId),
                        });
                      }
                      if (canRerun) {
                        menuItems.push({
                          key: 'rerun',
                          label: lifecycle === 'failed' ? 'Retry' : 'Re-run',
                          onSelect: () => onRerunScan(r.scanId, scheduleScanType),
                        });
                      }
                      if (canResume) {
                        menuItems.push({
                          key: 'resume',
                          label: 'Resume',
                          onSelect: () => onResumeScan(r.scanId, r.scannerId),
                        });
                      }

                      if (!canDelete && menuItems.length === 0) {
                        return <span className={styles.actionsEmpty}>—</span>;
                      }

                      const deleteLabel = isSchedule ? 'Delete this schedule' : 'Delete this historical run';

                      return (
                        <>
                          {canDelete ? (
                            <button
                              type="button"
                              className={[styles.deleteBtn, styles.actionIconBtn].filter(Boolean).join(' ')}
                              title={deleteLabel}
                              aria-label={deleteLabel}
                              onClick={(e) => {
                                e.stopPropagation();
                                closeRowActionsMenu();
                                onDeleteScan([
                                  {
                                    scanId: r.scanId,
                                    status: r.status,
                                    taskLifecycle: r.taskLifecycle,
                                    recordKind: isSchedule ? 'schedule' : 'run',
                                    scanType: scheduleScanType,
                                  },
                                ]);
                              }}
                            >
                              <TrashIcon className={styles.actionIcon} size={16} strokeWidth={2.1} />
                            </button>
                          ) : null}

                          {menuItems.length > 0 ? (
                            <div
                              className={styles.rowActionsMenuWrap}
                              ref={menuOpen ? rowActionsTriggerRef : null}
                            >
                              <button
                                type="button"
                                className={[
                                  styles.moreBtn,
                                  styles.actionIconBtn,
                                  menuOpen ? styles.moreBtnOpen : null,
                                ].filter(Boolean).join(' ')}
                                title="More actions"
                                aria-label="More actions"
                                aria-haspopup="menu"
                                aria-expanded={menuOpen}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const trigger = e.currentTarget;
                                  if (!trigger) return;
                                  const rect = trigger.getBoundingClientRect();
                                  const isLightTheme = Boolean(
                                    document.querySelector('.dashboard-container.theme-light')
                                  );
                                  setOpenRowMenuKey((current) => {
                                    if (current === menuKey) {
                                      setRowMenuPlacement(null);
                                      return null;
                                    }
                                    const estimatedMenuHeight = Math.max(52, menuItems.length * 36 + 20);
                                    const openUp = window.innerHeight - rect.bottom < estimatedMenuHeight + 16;
                                    setRowMenuPlacement({
                                      top: openUp ? rect.top - 4 : rect.bottom + 4,
                                      right: Math.max(8, window.innerWidth - rect.right),
                                      openUp,
                                      isLightTheme,
                                    });
                                    return menuKey;
                                  });
                                }}
                              >
                                <svg
                                  className={styles.moreIcon}
                                  viewBox="0 0 20 20"
                                  width="14"
                                  height="14"
                                  fill="currentColor"
                                  aria-hidden="true"
                                >
                                  <circle cx="4" cy="10" r="1.6" />
                                  <circle cx="10" cy="10" r="1.6" />
                                  <circle cx="16" cy="10" r="1.6" />
                                </svg>
                              </button>
                              {menuOpen && rowMenuPlacement
                                ? createPortal(
                                    <div
                                      ref={rowActionsMenuRef}
                                      className={[
                                        styles.rowActionsMenu,
                                        styles.rowActionsMenuPortal,
                                        rowMenuPlacement.openUp ? styles.rowActionsMenuPortalUp : null,
                                        rowMenuPlacement.isLightTheme ? styles.rowActionsMenuLight : null,
                                      ].filter(Boolean).join(' ')}
                                      role="menu"
                                      style={{
                                        top: rowMenuPlacement.top,
                                        right: rowMenuPlacement.right,
                                      }}
                                    >
                                      {menuItems.map((item) => (
                                        <button
                                          key={item.key}
                                          type="button"
                                          role="menuitem"
                                          className={styles.rowActionsMenuItem}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            closeRowActionsMenu();
                                            item.onSelect();
                                          }}
                                        >
                                          {item.label}
                                        </button>
                                      ))}
                                    </div>,
                                    document.body
                                  )
                                : null}
                            </div>
                          ) : null}
                        </>
                      );
                    })()}
                  </div>
                </td>
              </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={scanTableColumns.length} className="assets-empty-cell">
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
    </table>
  );

  const searchControl = toolbarSearch ? (
    <div className="subscription-org-management-search" data-tour="scans-search">
      <label htmlFor={tableSearchId} className="edi-sr-only">
        {toolbarSearchPlaceholder}
      </label>
      <div className="subscription-org-management-search-inner">
        <input
          id={tableSearchId}
          type="search"
          className="subscription-org-management-search-input"
          value={searchQuery}
          onChange={(e) => applyToolbarSearch(e.target.value)}
          placeholder={toolbarSearchPlaceholder}
          autoComplete="off"
          spellCheck={false}
          maxLength={UNIVERSAL_SEARCH_MAX_LEN}
        />
        {searchQuery ? (
          <button
            type="button"
            className="ghost-exposure-sys-table-search-clear"
            onClick={() => applyToolbarSearch('')}
            aria-label="Clear search"
          >
            ✕
          </button>
        ) : null}
      </div>
    </div>
  ) : null;

  const loadingPanel = (
    <div className={[styles.tableCard, 'scans-table-card'].filter(Boolean).join(' ')}>
      <div className={styles.loadingPanel}>
        <BlueXLoader />
      </div>
    </div>
  );

  const tableShell = isLoading ? loadingPanel : (
    <div className={[styles.tableCard, 'scans-table-card'].filter(Boolean).join(' ')} data-tour="scans-table">
      <div className={["assets-table-shell", styles.wrap, "assets-table-shell--inventory-body"].filter(Boolean).join(' ')}>
        <div
          className={styles.headerFrame}
          style={{ '--scans-header-gutter': `${headerGutterPx}px` }}
        >
          <div ref={tableHeaderRef} className={styles.headerScroll}>
            {headerTable}
          </div>
          <div className={styles.headerGutter} aria-hidden="true" />
        </div>
        <div
          ref={tableScrollRef}
          className={[styles.bodyScroll, 'assets-inventory-table-scroll', 'scans-table-body-scroll'].filter(Boolean).join(' ')}
        >
          {bodyTable}
        </div>
      </div>
    </div>
  );

  const pagination = !isLoading ? (
    <div className={styles.paginationOutside} data-tour="scans-pagination">
      <TablePagination
        {...scanPagination}
        itemLabel={itemLabel}
      />
    </div>
  ) : null;

  if (toolbarSearch) {
    const headerRowClass = [
      'subscription-org-management-header',
      styles.networkSplitHeader,
      sectionHeading ? null : styles.networkSplitHeaderSearchOnly,
    ].filter(Boolean).join(' ');
    return (
      <div className={[styles.networkSplitStack, 'scans-network-split-stack'].filter(Boolean).join(' ')}>
        <div className={headerRowClass}>
          {sectionHeading ? (
            <div>
              <h3
                id={sectionHeadingId || fallbackSectionHeadingId}
                className={[styles.networkSplitHeading, styles.networkSplitTableHeading].filter(Boolean).join(' ')}
              >
                {sectionHeading}
              </h3>
            </div>
          ) : null}
          <div className="subscription-org-management-actions">
            {toolbarLeadingContent}
            {bulkActions}
            {searchControl}
          </div>
        </div>
        {tableShell}
        {pagination}
      </div>
    );
  }

  return (
    <>
      {bulkActions}
      {tableShell}
      {pagination}
    </>
  );

}
